import { ApiService, SYNC_INTERVAL } from './api.service';
import { Injectable, OnDestroy } from '@angular/core';

import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { ISettings } from '@models/settings';
import { Preferences } from '@capacitor/preferences';
import { User } from '@models/user';
import { cloneDeep } from 'lodash-es';
import { SyncStatusService } from './sync-status.service';
import { environment } from '../../environments/environment';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { version } from '../../environments/version';

declare global {
  interface Navigator {
    msSaveOrOpenBlob: (blobOrBase64: Blob | string, filename: string) => void;
  }
}

/**
 * Replace forward and backward strings and spaces with underscore to
 * make a string that can be used as a filename on all platforms.
 *
 * @param {*} str
 * @returns String
 */
const safeFileName = (str) => {
  return str.trim().replace(/[/\\ ]/g, '_');
};

/**
 * Extract the file name string from the HTTP response content-disposition
 * header's attachment. If it can't be extracted return the defaultFilename.
 *
 * @param {*} header
 * @param {*} defaultFilename
 * @returns
 */
const attachmentFileName = (contentDisposition, defaultFilename) => {
  try {
    const fileName = contentDisposition // "attachment; filename="export_a8edbf.xls""
      .split('filename=')[1]
      .replace(/["']/g, '');
    return fileName;
  } catch (e) {
    return defaultFilename;
  }
};

/**
 * Download a document obtained via the API by adding a temporary
 * link to the DOM and clicking it (just like a user would).
 *
 * @param {*} blob
 * @param {*} fileName
 * @param {*} content_type
 */
const downloadDocument = (blob, fileName, type = 'application/pdf') => {
  const newBlob = new Blob([blob], { type });
  const navigator = window.navigator;
  if (navigator?.msSaveOrOpenBlob) {
    // DEPRECATED: ??
    navigator.msSaveOrOpenBlob(newBlob, fileName);
    return;
  }
  const data = window.URL.createObjectURL(newBlob);
  const link = document.createElement('a');
  link.href = data;
  link.download = safeFileName(fileName);
  document.body.append(link);
  link.click();
  // Return resources/revert temporary document changes
  setTimeout(() => {
    window.URL.revokeObjectURL(data);
    document.body.removeChild(link);
  }, 500);
};

@Injectable({
  providedIn: 'root',
})
export class UserDataService extends SyncStatusService implements OnDestroy {
  user: User = null;
  user$: BehaviorSubject<User> = null;
  lastProfile: User = null; // override user.id to debug operations as that User
  protected URL = 'users/profiles';

  constructor(
    private http: HttpClient,
    private api: ApiService,
  ) {
    super();
    this.user$ = new BehaviorSubject<User>(this.user);
  }

  ngOnDestroy(): void {
    this._clearSyncTimeout();
  }

  hasLayoutFavorite(id: string): boolean {
    return this.user?.settings?.layoutFavorites?.includes(id) ?? false;
  }

  addMasters(): void {
    // temporarily add and push so all subscribers see change
    // immediately. Otherwise, would have to wait for next
    // polling interval with the server to see the change.
    this.user.subscriptions = [
      {
        plan_name: 'Masters Subscription',
        status: 'processing',
        billing_type: '',
        cancel_url: null,
        currency: '',
        event_time: '',
        next_bill_date: '',
        source: '',
        unit_price: null,
        update_url: '',
      },
      ...this.user.subscriptions,
    ];
    this._publish();
  }

  async addLayoutFavorite(id: string): Promise<void> {
    try {
      this.user.settings.layoutFavorites.push(id);
      await this._updateUser(this.user);
    } catch (e) {
      // remove pushed change
      this.user.settings.layoutFavorites.pop();
      throw e;
    }
  }

  async removeLayoutFavorite(id: string): Promise<void> {
    try {
      const index = this.user.settings.layoutFavorites.indexOf(id);
      if (index > -1) {
        this.user.settings.layoutFavorites.splice(index, 1);
        await this._updateUser(this.user);
      }
    } catch (e) {
      // remove pushed change
      this.user.settings.layoutFavorites.push(id);
      throw e;
    }
  }

  hasMapFavorite(id: string): boolean {
    return this.user?.settings?.mapFavorites?.includes(id) ?? false;
  }

  async addMapFavorite(id: string): Promise<void> {
    try {
      this.user.settings.mapFavorites.push(id);
      await this._updateUser(this.user);
    } catch (e) {
      // remove pushed change
      this.user.settings.mapFavorites.pop();
      throw e;
    }
  }

  async removeMapFavorite(id: string): Promise<void> {
    try {
      const index = this.user.settings.mapFavorites.indexOf(id);
      if (index > -1) {
        this.user.settings.mapFavorites.splice(index, 1);
        await this._updateUser(this.user);
      }
    } catch (e) {
      // remove pushed change
      this.user.settings.mapFavorites.push(id);
      throw e;
    }
  }

  getUsername(): string {
    return this?.user?.username() ?? 'No Username';
  }

  hasSeenTutorial(): boolean {
    return this?.user?.settings?.hasSeenTutorial ?? false;
  }

  async sawTutorial(): Promise<void> {
    if (this.user?.settings) {
      this.user.settings.hasSeenTutorial = true;
      await this._updateUser(this.user);
    }
  }

  hasSeenLayoutVisualEditHelp(): boolean {
    return this?.user?.settings?.hasSeenLayoutVisualEditHelp ?? false;
  }

  async sawLayoutVisualEditHelp(): Promise<void> {
    if (this.user?.settings) {
      this.user.settings.hasSeenLayoutVisualEditHelp = true;
      await this._updateUser(this.user);
    }
  }

  hasSeenLayoutViewHelp(): boolean {
    return this.user?.settings?.hasSeenLayoutViewHelp ?? false;
  }

  async sawLayoutViewHelp(): Promise<void> {
    if (this.user?.settings) {
      this.user.settings.hasSeenLayoutViewHelp = true;
      await this._updateUser(this.user);
    }
  }

  hasSeenMapDesignViewHelp(): boolean {
    return this.user?.settings?.hasSeenMapDesignViewHelp ?? false;
  }

  async sawMapDesignViewHelp(): Promise<void> {
    if (this.user?.settings) {
      this.user.settings.hasSeenMapDesignViewHelp = true;
      await this._updateUser(this.user);
    }
  }

  async updateSettings(settings: ISettings): Promise<void> {
    Object.assign(this.user.settings, settings);
    await this._updateUser(this.user);
  }

  override async liveSync(): Promise<void> {
    try {
      this._clearSyncTimeout();
      this.synchronizing();
      // _getLatestFromApi() will publish()
      await this._getLatestFromApi();
      this.synchronized();
    } catch (e) {
      console.error(e);
      if (e.status === 401) {
        this.syncUnauthorized();
      } else {
        this.syncError();
      }
    } finally {
      this._syncTimeoutHandle = setTimeout(
        () => this.liveSync(),
        SYNC_INTERVAL,
      );
    }
  }

  // used on reconnect to server to get latest data
  public async load(): Promise<User> {
    return this._load(this.user.id);
  }

  public delete(): Promise<boolean> {
    return this.api.delete<User>(this.URL, this.user.id);
  }

  async loadFromAuthProfile(profile: unknown): Promise<void> {
    await this._load(profile['https://agilitycoursemaster.com/userIdentifier']);
    // TODO: delete this code at next major version
    await Preferences.migrate();
    await Preferences.removeOld();
    await Preferences.set({ key: 'user_signedUp', value: 'true' }); // TODO: put in api database
  }

  loadAsUser(userId: string): Promise<User> {
    // Used to change user on Account page
    this.lastProfile = new User({ ...cloneDeep(this.user), _id: userId });
    // replace userId so we access that user's data
    // but don't save the modified profile
    return this._load(userId);
  }

  async exportData(): Promise<void> {
    const options = {
      headers: new HttpHeaders({
        'Content-Type': 'application/zip',
      }),
    };
    const response = await firstValueFrom(
      this.http.get(`${environment.acmAPI}users/export/?ngsw-bypass=true`, {
        ...options,
        responseType: 'arraybuffer',
        observe: 'response',
      }),
    );
    const downloadFileName = attachmentFileName(
      response.headers.get('content-disposition'),
      'acmaster-export.zip',
    );
    downloadDocument(
      response.body,
      downloadFileName,
      response.headers.get('content-type'),
    );
  }

  logout(): void {
    this.user = null;
    this._publish();
    this.syncError();
  }

  sawLatestReleaseNotes(): void {
    if (this.user?.settings) {
      this.user.settings.releaseNotesVersionSeen = version.version;
      this._updateUser(this.user);
    }
  }

  hasSeenLatestReleaseNotes(): boolean {
    return this.user?.settings?.releaseNotesVersionSeen === version.version;
  }

  async hasSignedUp(): Promise<boolean> {
    const result = await Preferences.get({ key: 'user_signedUp' });
    return result.value === 'true';
  }

  protected async _getLatestFromApi(): Promise<User[]> {
    if (!this.user) {
      return [];
    }
    await this._loadUser(this.user.id);
    return [this.user];
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  protected _addUpdate(payload: User) {
    // this is unused - just to satisfy abstract method
    return;
  }

  protected _publish(): void {
    try {
      this.user$.next(this.user);
    } catch (e) {
      console.error('Recreating user$ - check unsubscribes');
      this.user$ = new BehaviorSubject<User>(this.user);
      this.user$.next(this.user);
    }
  }

  private _load(userId: string): Promise<User> {
    // See if user exists in db:
    return this._loadUser(userId)
      .then((user: User) => {
        this.user = user;
      })
      .catch((e) => {
        // put new User in database
        this.user = new User({
          id: userId,
        });
        console.error(e);
      })
      .then(() => {
        this._publish();
        return this.user;
      });
  }

  private _loadUser(id: string): Promise<User> {
    return this.api.get<User>(this.URL, id).then((doc: unknown) => {
      this.user = new User(doc);
      this._publish();
      return this.user;
    });
  }

  private async _updateUser(user: User): Promise<User> {
    console.debug('_updateUser', user);
    try {
      const updated = await this.api.put<User>(
        this.URL,
        user.id,
        user,
        user.updated,
      );
      this.user = new User(updated);
      this._publish();
    } catch (e) {
      if (e.status === 409) {
        // discard local edit by reloading current user profile
        return await this._loadUser(user.id);
      }
      throw e;
    }
    return this.user;
  }
}
